// ---------- le serveur ----------
// Le contenu (chapitres, thèmes, règles), une partie, et le mot du jour. Tout ce que le jeu
// affiche vient d'ici : c'est ce qui permet d'ouvrir un chapitre sans mise à jour Android. Le jeu
// n'écrit rien ici, sinon la demande du mot du jour — la progression vit sur l'appareil
// (src/store.ts), et sa copie en ligne passe par le compte (src/account.ts, src/sync.ts).
//
// HORS LIGNE : chaque réponse est recopiée dans le localStorage, et resservie quand le réseau
// manque. Une partie déjà ouverte se finit dans le métro ; une partie jamais ouverte, non — l'écran
// le dit, au lieu de laisser croire qu'elle existe.

import { CONTENT_FORMAT, type Content, type PuzzlePayload } from '../shared/types';

/** L'URL du Worker. En dev, celle de `wrangler dev` ; au build de production, VITE_API_URL
 *  (scripts/deploy-web.sh, scripts/sync-android.sh). */
export const API = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:8787';

const CACHE = 'verbigrid:api:';

function remember(path: string, body: unknown): void {
  try {
    localStorage.setItem(CACHE + path, JSON.stringify(body));
  } catch {
    /* stockage plein ou refusé : on joue sans copie hors ligne */
  }
}

function recall<T>(path: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE + path);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    const res = await fetch(API + path, { ...init, headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = (await res.json()) as T;
    remember(path, body);
    return body;
  } catch (e) {
    const old = recall<T>(path);
    if (old) return old;
    throw e;
  }
}

/** Le contenu. Un `format` plus récent que celui que ce jeu connaît veut dire que des champs ont
 *  changé de sens : on le lit quand même (ce qu'on connaît n'a pas bougé), et `newer` le signale
 *  pour que le jeu propose la mise à jour de l'application. */
export async function fetchContent(lang: string): Promise<Content & { newer: boolean }> {
  const c = await getJson<Content>(`/content?lang=${encodeURIComponent(lang)}`);
  return { ...c, newer: c.format > CONTENT_FORMAT };
}

export const fetchPuzzle = (id: string): Promise<PuzzlePayload> =>
  getJson(`/puzzles/${encodeURIComponent(id)}`);

/** Le mot du jour : le serveur tire une partie au premier joueur du jour, et la même pour tous
 *  ensuite. Hors ligne, on ressert celle d'aujourd'hui si on l'a déjà eue — jamais celle d'hier,
 *  qui se ferait passer pour le mot du jour. */
export async function fetchDaily(
  lang: string,
): Promise<{ day: string; puzzleId: string } | null | 'offline'> {
  // Déjà tiré aujourd'hui : la réponse ne changera plus de la journée (le serveur la fige pour
  // tous), inutile de la redemander à chaque ouverture de la carte.
  const today = new Date().toISOString().slice(0, 10);
  const known = recall<{ day: string; puzzleId: string }>(
    `/daily?lang=${encodeURIComponent(lang)}`,
  );
  if (known?.day === today) return known;
  try {
    const d = await getJson<{ day: string; puzzleId: string }>(
      `/daily?lang=${encodeURIComponent(lang)}`,
      { method: 'POST' },
    );
    return d.day === new Date().toISOString().slice(0, 10) ? d : null;
  } catch (e) {
    // Un refus du serveur (stock vide : 404) veut dire « pas de mot aujourd'hui » ; une panne de
    // réseau, « reviens quand tu auras du réseau ». Les confondre disait « reviens demain » à un
    // joueur dans le métro (revue de code, 2026-10-02).
    if (e instanceof Error && e.message.startsWith('HTTP ')) return null;
    return 'offline';
  }
}
